/* eslint-disable react/prop-types */
import React from "react";
import { Text } from "@imminently/react/ui/core/text";
import Stack from "@mui/material/Stack";
import InputBase from "@mui/material/InputBase";
import FormHelperText from "@mui/material/FormHelperText";
import type { PatientEncounter } from "@/resources/types";
import { styled } from "@mui/material/styles";
import { medicareObjSchema } from "@/modals/addPatient/medicareValidate";
import { encounterService } from "@/services/encounter";
import debounce from "lodash/debounce";
import { DebouncedFunc } from "lodash";
import { useEncounter } from "@/resources/encounters";
import DelegateDataTooltip from "./Patient/DelegateDataTooltip";

type FormValues = Pick<PatientEncounter["patient"], "medicareNo" | "medicareNoPos">;
const validateFormValues = (v: FormValues): string | true => {
  try {
    medicareObjSchema.validateSync(v);

    return true;
  } catch (e) {
    if (e instanceof Error) return e.message;

    throw e;
  }
};

// # region EditablePart

const UnderlinedInputBase = styled(InputBase)`
  border-bottom: 1px solid ${p => p.theme.palette.primary.main};

  &.no { width: 6rem; }
  &.pos { width: 1.25rem; }
`;

type EditableInputProps = {
  encounter: PatientEncounter;
  switchEditingModeOff: () => unknown;
}
const EditablePart = React.memo<EditableInputProps>(p => {
  const { encounter, encounter: { id }, switchEditingModeOff } = p;

  const { mutateAsync: updateEncounter } = encounterService.useUpdate();
  type DebouncedUpdEnc = DebouncedFunc<(arg: FormValues) => unknown>;
  const debouncedUpdateEncounter = React.useMemo<DebouncedUpdEnc>(() => debounce(patient => {
    updateEncounter({ id, payload: { patient: patient as any } });
  }, 500), [updateEncounter, id]);


  /**
   * will signalize whether we focused at least\
   * one field in edit mode. And if yes - only\
   * then we should apply validation
   */
  const [isTouched, setIsTouched] = React.useState(false);
  const setTouchedOnFocus = React.useCallback(() => setIsTouched(true), [isTouched]);


  const medicareNoRef = React.useRef<HTMLInputElement>(null);
  const medicareNoPosRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (medicareNoRef.current) medicareNoRef.current.focus();
  }, []);

  const { medicareNo, medicareNoPos } = encounter.patient;
  const [values, setValues] = React.useState<FormValues>({ medicareNo, medicareNoPos });
  const valuesRef = React.useRef(values);
  if (valuesRef.current !== values) valuesRef.current = values;


  const onChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(e => {
    const { currentTarget: c, currentTarget: { value } } = e;
    const { current: cur } = valuesRef;

    const nextValues: typeof cur = {
      ...cur,
      ...(c === medicareNoRef.current ? { medicareNo: value } : {}),
      ...(c === medicareNoPosRef.current ? { medicareNoPos: value } : {}),
    };

    setValues(nextValues);

    {
      const result = validateFormValues(nextValues);
      if (result !== true) {
        debouncedUpdateEncounter.cancel();

        return;
      }

      debouncedUpdateEncounter(nextValues);
    }
  }, [setValues, debouncedUpdateEncounter]);


  const error = React.useMemo(() => {
    const result = validateFormValues(values);

    return typeof result === "string" ? result : "";
  }, [values]);


  const maybeSwitchEditModeOffOnBlur = React.useCallback<React.FocusEventHandler>(e => {
    const { relatedTarget: rel } = e;

    if (rel !== null) {
      if (rel === medicareNoRef.current || rel === medicareNoPosRef.current) return;
    }

    switchEditingModeOff();
  }, [switchEditingModeOff]);


  return (
    <Stack>
      <Stack direction='row' alignItems='center' gap='0.5rem'>
        <UnderlinedInputBase
          value={values.medicareNo || ""}
          onFocus={setTouchedOnFocus}
          onBlur={maybeSwitchEditModeOffOnBlur}
          onChange={onChange}
          className="no"
          inputRef={medicareNoRef}
        />

        <span>-</span>

        <UnderlinedInputBase
          value={values.medicareNoPos || ""}
          onFocus={setTouchedOnFocus}
          onBlur={maybeSwitchEditModeOffOnBlur}
          inputRef={medicareNoPosRef}
          onChange={onChange}
          className="pos"
        />
      </Stack>
      {isTouched && error ? <FormHelperText error>{error}</FormHelperText> : null}
    </Stack>
  );
});
EditablePart.displayName = "components/PatientDetailBlock/Medicare/EditablePart";

// # endregion


// #region CombinedInput

type CombinedInputProps = Pick<EditableInputProps, "encounter"> & { disabled?: boolean };
const CombinedInput = React.memo<CombinedInputProps>(({ encounter, disabled }) => {
  const { medicareNo, medicareNoPos } = encounter.patient || {};
  const { isFetching } = useEncounter(encounter.id);


  const [isEditing, setIsEditing] = React.useState(false);
  const switchToEditMode = React.useCallback(() => setIsEditing(true), [setIsEditing]);
  const switchEditingModeOff = React.useCallback(() => setIsEditing(false), [setIsEditing]);


  const full = React.useMemo(() => {
    if (medicareNo === undefined || medicareNo === "") return "";
    if (medicareNoPos === undefined || medicareNoPos === "") return medicareNo;

    return `${medicareNo}-${medicareNoPos}`;
  }, [medicareNo, medicareNoPos]);


  if (isEditing) {
    return <EditablePart {...{ encounter, switchEditingModeOff }} />;
  }

  return (
    <InputBase
      value={full}
      disabled={isFetching || disabled}
      onFocus={switchToEditMode}
    />
  );
});
CombinedInput.displayName = "components/PatientDetailBlock/Medicare/CombinedInput";

// #endregion

export type MedicareProps = CombinedInputProps;


export const Medicare = (props: MedicareProps) => {
  const { encounter, disabled } = props;

  return (
    <Stack paddingTop='0.5rem' paddingBottom='0.5rem'>
      <Text variant="h6">MEDICARE</Text>
      <DelegateDataTooltip encounter={encounter} field="medicareNo" value={encounter.patient?.medicareNo} >
        <CombinedInput encounter={encounter} disabled={disabled} />
      </DelegateDataTooltip>
    </Stack>
  );
};
